import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Alert, Button, Card, Table, Form } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import { paymentFileService } from '../services/paymentFileService';
import AccessDenied from '../components/common/AccessDenied';
import DeleteConfigItemModal from '../components/config/modals/DeleteConfigItemModal';

const PaymentFileConfigPage = () => {
  const navigate = useNavigate();
  const { isMasterAdmin } = useAuth();

  if (!isMasterAdmin) {
    return <AccessDenied className="payment-file-config-page" />;
  }

  const { isInitialized, showLoading, hideLoading } = useUI();
  
  const [config, setConfig] = useState(null);

  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // New company form
  const [newCompanyShort, setNewCompanyShort] = useState('');
  const [newCompanyLong, setNewCompanyLong] = useState('');
  const [addingCompany, setAddingCompany] = useState(false);

  // New fund type form
  const [newFundTypeShort, setNewFundTypeShort] = useState('');
  const [newFundTypeLong, setNewFundTypeLong] = useState('');
  const [addingFundType, setAddingFundType] = useState(false);

  // Delete confirmation
  const [deleteItem, setDeleteItem] = useState(null);

  // Generate files
  const [generating, setGenerating] = useState(false);


  const loadConfig = async () => {
    showLoading('Loading configuration...');
    setError('');

    try {
      const response = await paymentFileService.getConfig();
      if (response.success) {
        setConfig({
          companies: response.companies || [],
          fundTypes: response.fundTypes || [],
          fileNames: response.fileNames || [],
        });
      } else {
        setError(response.error || 'Failed to load configuration.');
      }
    } catch {
      setError('An error occurred while loading configuration.');
    } finally {
      hideLoading();
    }
  }

  useEffect(() => {
    if (isInitialized && isMasterAdmin) {
      loadConfig();
    }
  }, [isInitialized, isMasterAdmin]);

  if (!config && !error) {
    // Display nothing when data is not ready
    return <></>;
  }

  const handleAddCompany = async (e) => {
    e.preventDefault();
    if (!newCompanyShort.trim() || !newCompanyLong.trim()) {
      setError('Company name and abbreviation are required.');
      return;
    }

    setAddingCompany(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await paymentFileService.addCompany(newCompanyShort.trim(), newCompanyLong.trim());
      if (response.success) {
        setSuccessMessage('Company added successfully.');
        setNewCompanyShort('');
        setNewCompanyLong('');
        await loadConfig();
      } else {
        setError(response.error || 'Failed to add company.');
      }
    } catch (err) {
      setError('An error occurred while adding company.');
    } finally {
      setAddingCompany(false);
    }
  };

  const handleAddFundType = async (e) => {
    e.preventDefault();
    if (!newFundTypeShort.trim() || !newFundTypeLong.trim()) {
      setError('Fund type name and abbreviation are required.');
      return;
    }

    setAddingFundType(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await paymentFileService.addFundType(newFundTypeShort.trim(), newFundTypeLong.trim());
      if (response.success) {
        setSuccessMessage('Fund type added successfully.');
        setNewFundTypeShort('');
        setNewFundTypeLong('');
        await loadConfig();
      } else {
        setError(response.error || 'Failed to add fund type.');
      }
    } catch (err) {
      setError('An error occurred while adding fund type.');
    } finally {
      setAddingFundType(false);
    }
  };

  const handleDeleteClick = (item, type) => {
    setDeleteItem({ ...item, type });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteItem) return { success: false, error: 'No item selected.' };

    setError('');
    setSuccessMessage('');

    try {
      let response;
      if (deleteItem.type === 'company') {
        response = await paymentFileService.deleteCompany(deleteItem.id);
      } else {
        response = await paymentFileService.deleteFundType(deleteItem.id);
      }

      if (response.success) {
        setSuccessMessage(`${deleteItem.type === 'company' ? 'Company' : 'Fund type'} deleted successfully.`);
        setDeleteItem(null);
        await loadConfig();
        return { success: true };
      } else {
        return { success: false, error: response.error || 'Failed to delete item.' };
      }
    } catch (err) {
      return { success: false, error: 'An error occurred while deleting.' };
    }
  };

  const handleGenerateFiles = async () => {
    if (!window.confirm('Are you sure you want to manually generate Payment & Rate Discount files?')) {
      return;
    }

    setGenerating(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await paymentFileService.generateFiles();
      if (response.success) {
        setSuccessMessage('Payment & Rate Discount files generated successfully.');
      } else {
        setError(response.error || 'Failed to generate Payment & Rate Discount files.');
      }
    } catch (err) {
      setError('An error occurred while generating Payment & Rate Discount files.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Container className="payment-file-config-page">
      <div className="d-flex justify-content-between align-items-center mb-3">
          <h2>Payment & Rate Discount File Config</h2>
          <div className="d-flex gap-2">
            <Button
              variant="primary"
              onClick={handleGenerateFiles}
              disabled={generating}
            >
              {generating ? 'Generating...' : 'Create Files Manually'}
            </Button>
          </div>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert variant="success" dismissible onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        )}

        {/* File Names */}
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">File Names</h5>
          </Card.Header>
          <Card.Body>
            {config.fileNames.length > 0 ? (
              <div className="table-responsive">
                <Table striped bordered className="mb-0">
                  <tbody>
                    {config.fileNames.map((fileName, index) => (
                      <tr key={index}>
                        <td>{fileName}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <p className="text-muted mb-0">No file names configured.</p>
            )}
          </Card.Body>
        </Card>

        {/* Company List */}
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">Company List</h5>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <Table striped bordered hover className="mb-0">
                <thead>
                  <tr>
                    <th>Company Name</th>
                    <th>Abbreviation</th>
                    <th className="text-center" style={{ width: '120px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {config.companies.map((company) => (
                    <tr key={company.id}>
                      <td className="align-content-center">{company.companyLong}</td>
                      <td className="align-content-center">{company.companyShort}</td>
                      <td  className="text-center">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteClick(company, 'company')}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td>
                      <Form.FloatingLabel label="Long Name" controlId="company-long-name">
                        <Form.Control
                          type="text"
                          placeholder="Long Name"
                          value={newCompanyLong}
                          onChange={(e) => setNewCompanyLong(e.target.value)}
                        />
                      </Form.FloatingLabel>
                    </td>
                    <td>
                      <Form.FloatingLabel label="Abbreviation" controlId="company-short-name">
                        <Form.Control
                          type="text"
                          placeholder="Abbreviation"
                          value={newCompanyShort}
                          onChange={(e) => setNewCompanyShort(e.target.value)}
                        />
                      </Form.FloatingLabel>
                    </td>
                    <td className="text-center align-content-center">
                      <Button
                        className="text-center"
                        variant="primary"
                        size="sm"
                        onClick={handleAddCompany}
                        disabled={addingCompany}
                      >
                        {addingCompany ? 'Adding...' : 'Add New'}
                      </Button>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>

        {/* Fund Type List */}
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              Fund Type List
              <small className="ms-2">
                (NOTE: new file locations will need to be manually added by a developer)
              </small>
            </h5>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <Table striped bordered hover className="mb-0">
                <thead>
                  <tr>
                    <th>Fund Type</th>
                    <th>Abbreviation</th>
                    <th className="text-center" style={{ width: '120px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {config.fundTypes.map((fundType) => (
                    <tr key={fundType.id}>
                      <td className="align-content-center">{fundType.fundTypeLong}</td>
                      <td className="align-content-center">{fundType.fundTypeShort}</td>
                      <td className="text-center">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteClick(fundType, 'fundType')}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td>
                      <Form.FloatingLabel label="Long Name" controlId="fund-type-long-name">
                        <Form.Control
                          type="text"
                          placeholder=""
                          value={newFundTypeLong}
                          onChange={(e) => setNewFundTypeLong(e.target.value)}
                        />
                      </Form.FloatingLabel>
                    </td>
                    <td>
                      <Form.FloatingLabel label="Abbreviation" controlId="fund-type-short-name">
                        <Form.Control
                          type="text"
                          placeholder=""
                          value={newFundTypeShort}
                          onChange={(e) => setNewFundTypeShort(e.target.value)}
                        />
                      </Form.FloatingLabel>
                    </td>
                    <td className="text-center align-content-center">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleAddFundType}
                        disabled={addingFundType}
                      >
                        {addingFundType ? 'Adding...' : 'Add New'}
                      </Button>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>

      <DeleteConfigItemModal
        show={!!deleteItem}
        item={deleteItem}
        onConfirm={handleDeleteConfirm}
        onClose={() => setDeleteItem(null)}
      />
    </Container>
  );
};

export default PaymentFileConfigPage;
